import React, { useState, useCallback } from 'react';
import { 
  Plus, Trash2, Download, Upload, Sparkles, Grid3X3, LayoutGrid,
  Image as ImageIcon, Wand2, X, ZoomIn, Palette, Layers, FolderPlus,
  MoreHorizontal, Edit3, Copy, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';

const MoodBoardPage = () => {
  const [boards, setBoards] = useState([
    { 
      id: '1', 
      name: 'Modern Villa Project', 
      description: 'Inspiration for a contemporary waterfront residence',
      images: [],
      createdAt: new Date().toISOString()
    }
  ]);
  const [activeBoard, setActiveBoard] = useState(boards[0]?.id || null);
  const [showNewBoardDialog, setShowNewBoardDialog] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardDesc, setNewBoardDesc] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [generatingVariation, setGeneratingVariation] = useState(false);

  const currentBoard = boards.find(b => b.id === activeBoard);

  const handleCreateBoard = () => {
    if (!newBoardName.trim()) return;
    
    const newBoard = {
      id: Date.now().toString(),
      name: newBoardName,
      description: newBoardDesc,
      images: [],
      createdAt: new Date().toISOString()
    };
    
    setBoards([...boards, newBoard]);
    setActiveBoard(newBoard.id);
    setNewBoardName('');
    setNewBoardDesc('');
    setShowNewBoardDialog(false);
  };

  const handleDeleteBoard = (boardId) => {
    setBoards(boards.filter(b => b.id !== boardId));
    if (activeBoard === boardId) {
      setActiveBoard(boards[0]?.id || null);
    }
  };

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: Date.now().toString() + Math.random(),
          url: event.target.result,
          name: file.name,
          addedAt: new Date().toISOString()
        };
        
        setBoards(boards.map(b => 
          b.id === activeBoard 
            ? { ...b, images: [...b.images, newImage] }
            : b
        ));
      };
      reader.readAsDataURL(file);
    });
  }, [activeBoard, boards]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    
    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: Date.now().toString() + Math.random(),
          url: event.target.result,
          name: file.name,
          addedAt: new Date().toISOString()
        };
        
        setBoards(boards.map(b => 
          b.id === activeBoard 
            ? { ...b, images: [...b.images, newImage] }
            : b
        ));
      };
      reader.readAsDataURL(file);
    });
  }, [activeBoard, boards]);

  const handleDeleteImage = (imageId) => {
    setBoards(boards.map(b => 
      b.id === activeBoard 
        ? { ...b, images: b.images.filter(img => img.id !== imageId) }
        : b
    ));
  };

  const handleGenerateVariation = async (imageUrl) => {
    setGeneratingVariation(true);
    // Simulate AI generation - in production this would call the backend
    setTimeout(() => {
      const newImage = {
        id: Date.now().toString(),
        url: imageUrl, // In production, this would be the AI-generated variation
        name: 'AI Variation',
        addedAt: new Date().toISOString(),
        isGenerated: true
      };
      
      setBoards(boards.map(b => 
        b.id === activeBoard 
          ? { ...b, images: [...b.images, newImage] }
          : b
      ));
      setGeneratingVariation(false);
    }, 2000);
  };

  return (
    <div className="flex h-full bg-zinc-950">
      {/* Sidebar - Board List */}
      <div className="w-64 border-r border-zinc-800 flex flex-col">
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-400" />
              Mood Boards
            </h2>
          </div>
          
          <Dialog open={showNewBoardDialog} onOpenChange={setShowNewBoardDialog}>
            <DialogTrigger asChild>
              <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
                <FolderPlus className="h-4 w-4 mr-2" />
                New Board
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Mood Board</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Organize your design inspiration into themed collections.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-zinc-300">Board Name</Label>
                  <Input
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    placeholder="e.g., Modern Villa Project"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Description (optional)</Label>
                  <Textarea
                    value={newBoardDesc}
                    onChange={(e) => setNewBoardDesc(e.target.value)}
                    placeholder="Brief description of this mood board..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 resize-none"
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleCreateBoard}
                  disabled={!newBoardName.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-500"
                >
                  Create Board
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {boards.map((board) => (
              <div
                key={board.id}
                className={cn(
                  "group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all",
                  activeBoard === board.id 
                    ? "bg-purple-600/20 border border-purple-500/50" 
                    : "hover:bg-zinc-800/50"
                )}
                onClick={() => setActiveBoard(board.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm font-medium truncate",
                    activeBoard === board.id ? "text-purple-300" : "text-zinc-300"
                  )}>
                    {board.name}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">
                    {board.images.length} images
                  </p>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem className="text-zinc-300">
                      <Edit3 className="h-4 w-4 mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-zinc-300">
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-400 focus:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBoard(board.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {currentBoard ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-white">{currentBoard.name}</h1>
                  {currentBoard.description && (
                    <p className="text-sm text-zinc-400 mt-1">{currentBoard.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                    className="h-8 w-8"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'masonry' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('masonry')}
                    className="h-8 w-8"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="border-zinc-700 text-zinc-300">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
            </div>

            {/* Upload Area & Images */}
            <ScrollArea className="flex-1 p-4">
              {/* Drop Zone */}
              <div
                className="border-2 border-dashed border-zinc-700 hover:border-purple-500/50 rounded-xl p-8 mb-6 text-center transition-all cursor-pointer"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById('mood-upload')?.click()}
              >
                <input
                  id="mood-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Upload className="h-10 w-10 text-zinc-500 mx-auto mb-3" />
                <p className="text-zinc-300 font-medium">Drop images here or click to upload</p>
                <p className="text-sm text-zinc-500 mt-1">Collect inspiration for your design projects</p>
              </div>

              {/* Images Grid */}
              {currentBoard.images.length > 0 ? (
                <div className={cn(
                  "gap-4",
                  viewMode === 'grid' 
                    ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4" 
                    : "columns-2 md:columns-3 lg:columns-4 space-y-4"
                )}>
                  {currentBoard.images.map((image) => (
                    <Card 
                      key={image.id} 
                      className={cn(
                        "group relative overflow-hidden bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all",
                        viewMode === 'masonry' ? "break-inside-avoid mb-4" : ""
                      )}
                    >
                      <CardContent className="p-0">
                        <div className={cn(
                          "relative",
                          viewMode === 'grid' ? "aspect-square" : ""
                        )}>
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="text-xs text-white truncate mb-2">{image.name}</p>
                              <div className="flex items-center gap-2">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="secondary" className="h-7 text-xs">
                                      <ZoomIn className="h-3 w-3 mr-1" />
                                      View
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-2">
                                    <img src={image.url} alt={image.name} className="w-full rounded-lg" />
                                  </DialogContent>
                                </Dialog>
                                
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className="h-7 text-xs"
                                  onClick={() => handleGenerateVariation(image.url)}
                                  disabled={generatingVariation}
                                >
                                  <Wand2 className="h-3 w-3 mr-1" />
                                  {generatingVariation ? '...' : 'Vary'}
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className="h-7 text-xs text-red-400 hover:text-red-300"
                                  onClick={() => handleDeleteImage(image.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Generated Badge */}
                          {image.isGenerated && (
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 text-xs bg-purple-600 text-white rounded-full">
                                AI Generated
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                    <ImageIcon className="h-10 w-10 text-purple-400" />
                  </div>
                  <p className="text-zinc-400 text-lg">No images yet</p>
                  <p className="text-sm text-zinc-500 mt-1">Start collecting inspiration for your project</p>
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Palette className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-zinc-400 mb-2">No Mood Board Selected</h2>
              <p className="text-zinc-500 mb-4">Create a new mood board to start collecting inspiration</p>
              <Button 
                onClick={() => setShowNewBoardDialog(true)}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                Create Your First Board
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodBoardPage;
